import * as R from 'ramda';
import {
  ApiReducer,
  makeApiAction,
  makeApiReducer,
  RsaaTypes,
} from '../../../../core/redux/api/reduxStandardApiAction';
import { IAppState } from '../../../../core/redux/IAppState';
import { apiGetLiftBlackoutDatesMetadata } from '../../../../core/redux/api/endpoints';
import { ACTION_TYPE } from '../../../../core/redux/actionType';
import { addError, addLoading, getLoading, removeLoadingAndError } from '../../../../core/redux/api/module';
import { hasTrippedBreaker, updateBreaker } from '../../../../core/redux/api/circuitBreaker/module';
import { BlackoutDateMetadata } from '../../../../entitlementTypesMetadata/entitlementTypeMetadata';
import { isNilOrEmpty } from '../../../../language/module';
import { getResource } from '../../../../language/resources/getResource';


export const KEY_ACTION = ACTION_TYPE.GET_LIFT_BLACKOUTDATE_METADATA_REQUEST;
export const ACTIONS    = [
  ACTION_TYPE.GET_LIFT_BLACKOUTDATE_METADATA_REQUEST,
  ACTION_TYPE.GET_LIFT_BLACKOUTDATE_METADATA_RESPONSE,
  ACTION_TYPE.GET_LIFT_BLACKOUTDATE_METADATA_ERROR,
];


/**
 * Makes an action that can be dispatched to get a Lift blackout date metadata for a resort
 */
export const getLiftBlackoutDateMetadataAction = (resortAbbreviation: string) => makeApiAction(
  {
    method:   'GET',
    headers:  (state: IAppState) => ({
      'content-type':  'application/json',
      'Authorization': `Bearer ${state.auth.bearerToken}`,
    }),
    endpoint: apiGetLiftBlackoutDatesMetadata(resortAbbreviation),
    types:    ACTIONS.map(action => ({ type: action, meta: { resortAbbreviation } })) as RsaaTypes,
    // Bail if already loading results for that query
    bailout:  (state: IAppState) => hasTrippedBreaker(state, KEY_ACTION) || getLoading(state, resortAbbreviation, KEY_ACTION)?.requestId === resortAbbreviation,
  },
  );


const reducer: ApiReducer<BlackoutDateMetadata[]> = (state, action, error, isLoading, payload) => {
  const resort         = action.meta.resortAbbreviation;
  const appStateResort = R.path<string>(['selectedResort', 'resortAbbreviation'], state);

  // Ignore the results if the response is for a previous query that didn't complete before the
  // user switched resorts 
  if (action.type === ACTION_TYPE.GET_LIFT_BLACKOUTDATE_METADATA_RESPONSE && !isNilOrEmpty(appStateResort) && appStateResort !== resort) {
    const withBreaker = updateBreaker(state, KEY_ACTION, !!error);
    return removeLoadingAndError(withBreaker, resort, KEY_ACTION);
  }

  if (isLoading) {
    return addLoading(state, resort, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return addError(withBreaker, resort, KEY_ACTION, error ?? getResource(state.locale, 'GET_LIFT_BLACKOUTDATES_METADATA_ACTION_ERROR'));
  };

  if (!payload){
    payload = [];
  }

  const noLoadingOrError = removeLoadingAndError(withBreaker, resort, KEY_ACTION);

  return R.assoc('liftBlackoutDateMetadata', payload, noLoadingOrError);
};

export const getLiftBlackoutDateMetadataReducer = makeApiReducer<BlackoutDateMetadata[]>(ACTIONS, KEY_ACTION, reducer);
